/**
 * V74: 조직장 대시보드 - 팀원 근태 상태 조회 훅
 * V74.4: Firestore direct query → Cloud Function 경유로 전환
 *
 * CF `getTeamAttendanceStatus`를 호출하여
 * 서버에서 근태 상태를 계산한 결과를 받습니다.
 */

import { useState, useEffect, useMemo } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../firebase/config';
import { CompanyCode } from '../types/employee';

// ============================================
// 근태 상태 타입
// ============================================

export type AttendanceStatusLabel =
  | '출근'
  | '지각'
  | '미출근'
  | '퇴근'
  | '휴가'
  | '오전반차'
  | '오후반차'
  | '퍼스널힐링데이'
  | '대체휴무'
  | '-';

export interface AttendanceStatusInfo {
  label: AttendanceStatusLabel;
  color: string;
  background: string;
}

/** 상태별 스타일 */
const STATUS_STYLES: Record<AttendanceStatusLabel, { color: string; background: string }> = {
  '출근': { color: '#16a34a', background: '#dcfce7' },
  '지각': { color: '#ea580c', background: '#fff7ed' },
  '미출근': { color: '#dc2626', background: '#fef2f2' },
  '퇴근': { color: '#6b7280', background: '#f3f4f6' },
  '휴가': { color: '#7c3aed', background: '#f3e8ff' },
  '오전반차': { color: '#0284c7', background: '#e0f2fe' },
  '오후반차': { color: '#0284c7', background: '#e0f2fe' },
  '퍼스널힐링데이': { color: '#7c3aed', background: '#f3e8ff' },
  '대체휴무': { color: '#7c3aed', background: '#f3e8ff' },
  '-': { color: '#9ca3af', background: '#f3f4f6' },
};

// ============================================
// CF 요청/응답 타입
// ============================================

interface TeamAttendanceStatusRequest {
  employeeIds: string[];
  date?: string;
}

interface TeamAttendanceStatusResponse {
  success: boolean;
  statuses: Record<string, AttendanceStatusLabel>;
  today: string;
  nowHHmm: string;
}

// ============================================
// Hook
// ============================================

export interface UseTeamAttendanceStatusResult {
  statusMap: Map<string, AttendanceStatusInfo>;
  isLoading: boolean;
  error: Error | null;
}

/**
 * V74.4: 팀원 근태 상태 조회 훅 (CF 경유)
 *
 * @param employeeIds 조회 대상 직원 ID 목록
 * @param _companyCode 미사용 (하위호환 유지, 서버에서 타임존 처리)
 * @param _holidayDates 미사용 (하위호환 유지, 서버에서 공휴일 조회)
 */
export function useTeamAttendanceStatus(
  employeeIds: string[],
  _companyCode?: CompanyCode | undefined,
  _holidayDates?: Set<string>,
): UseTeamAttendanceStatusResult {
  const [statusMap, setStatusMap] = useState<Map<string, AttendanceStatusInfo>>(new Map());
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const idsKey = useMemo(() => [...employeeIds].sort().join(','), [employeeIds]);

  useEffect(() => {
    if (employeeIds.length === 0) {
      setStatusMap(new Map());
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    const fetchStatus = async () => {
      try {
        setIsLoading(true);
        setError(null);

        if (!functions) {
          throw new Error('Firebase Functions not initialized');
        }

        const fn = httpsCallable<TeamAttendanceStatusRequest, TeamAttendanceStatusResponse>(
          functions,
          'getTeamAttendanceStatus'
        );

        const result = await fn({ employeeIds });

        if (cancelled) return;

        if (result.data.success) {
          const newMap = new Map<string, AttendanceStatusInfo>();
          for (const [empId, label] of Object.entries(result.data.statuses)) {
            const style = STATUS_STYLES[label] || STATUS_STYLES['-'];
            newMap.set(empId, { label, ...style });
          }
          setStatusMap(newMap);
        } else {
          throw new Error('근태 상태 조회 실패');
        }
      } catch (err) {
        if (!cancelled) {
          console.error('[V74.4] useTeamAttendanceStatus error:', err);
          setError(err as Error);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchStatus();

    return () => {
      cancelled = true;
    };
  }, [idsKey]);

  return { statusMap, isLoading, error };
}
